// bm-design-system: multi-select primitive
import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { Check, ChevronsUpDown, Search, X } from "lucide-react"
import { cn } from "@/lib/utils"
import type { ComboboxOption } from "@/components/ui/combobox"

export type MultiSelectProps = {
  options: ComboboxOption[]
  values: string[]
  onChange: (values: string[]) => void
  /** Associates a `<label htmlFor>` with the trigger. */
  id?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  /** How the trigger reads once something is chosen. */
  summarize?: (count: number) => string
  disabled?: boolean
  align?: "start" | "center" | "end"
  className?: string
  contentClassName?: string
  triggerLabel?: string
  /** Names the chip list for screen readers, e.g. "Chosen District Councils". */
  selectionLabel?: string
}

/**
 * A dropdown you can type into that keeps as many options as you pick, showing
 * each choice as a removable chip beneath the trigger.
 *
 * Use it wherever a checkbox list would be right in principle but too long in
 * practice — a few dozen options and up. Below that, a checkbox list is easier
 * to scan because everything is visible at once.
 *
 * It is `<Combobox>` with the selection made plural: same popover, same type-to-
 * filter, same keyboard handling. The one behavioural difference is that
 * choosing an option does not close the list, because picking several things is
 * the point.
 */
export function MultiSelect({
  options,
  values,
  onChange,
  id,
  placeholder = "Select…",
  searchPlaceholder = "Type to filter…",
  emptyMessage = "No matches.",
  summarize,
  disabled = false,
  align = "start",
  className,
  contentClassName,
  triggerLabel,
  selectionLabel = "Selected",
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const [activeIndex, setActiveIndex] = React.useState(0)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)

  const selected = React.useMemo(
    () => options.filter((option) => values.includes(option.value)),
    [options, values],
  )

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter((option) => option.label.toLowerCase().includes(needle))
  }, [options, query])

  React.useEffect(() => {
    setActiveIndex(0)
  }, [query, open])

  // Keep the highlighted row in view while arrowing through a long list.
  React.useEffect(() => {
    if (!open) return
    const active = listRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    active?.scrollIntoView({ block: "nearest" })
  }, [activeIndex, open])

  // Chosen options keep the order the list presents them in, so the chips read
  // the same way twice regardless of what order they were picked.
  const toggle = (option: ComboboxOption) => {
    const next = values.includes(option.value)
      ? values.filter((value) => value !== option.value)
      : options
          .filter((each) => each.value === option.value || values.includes(each.value))
          .map((each) => each.value)
    onChange(next)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setActiveIndex((index) => Math.min(index + 1, filtered.length - 1))
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === "Enter") {
      event.preventDefault()
      const option = filtered[activeIndex]
      if (option) toggle(option)
    } else if (event.key === "Escape") {
      setOpen(false)
    }
  }

  const summary = summarize
    ? summarize(selected.length)
    : selected.length <= 2
      ? selected.map((option) => option.label).join(", ")
      : `${selected.length} selected`

  return (
    <div className="space-y-2">
      <PopoverPrimitive.Root
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setQuery("")
        }}
      >
        <PopoverPrimitive.Trigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            role="combobox"
            aria-expanded={open}
            aria-label={triggerLabel}
            className={cn(
              "form-control form-control-select flex w-full cursor-pointer items-center justify-between gap-2 text-left",
              disabled && "cursor-not-allowed opacity-60",
              className,
            )}
          >
            <span className={cn("truncate", selected.length === 0 && "text-ink-muted")}>
              {selected.length === 0 ? placeholder : summary}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-ink-muted" />
          </button>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align={align}
            sideOffset={6}
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              inputRef.current?.focus()
            }}
            className={cn(
              "z-50 w-[var(--radix-popover-trigger-width)] min-w-[12rem] overflow-hidden rounded-md border border-hairline bg-page shadow-lg",
              contentClassName,
            )}
          >
            <div className="flex items-center gap-2 border-b border-hairline px-3 py-2">
              <Search className="h-4 w-4 shrink-0 text-ink-muted" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="w-full bg-transparent text-sm text-ink-body outline-none placeholder:text-ink-muted"
              />
            </div>

            <div ref={listRef} role="listbox" aria-multiselectable className="max-h-64 overflow-y-auto p-1">
              {filtered.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-ink-muted">{emptyMessage}</p>
              )}
              {filtered.map((option, index) => {
                const isSelected = values.includes(option.value)
                const isActive = index === activeIndex
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-active={isActive}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => toggle(option)}
                    className={cn(
                      "flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm",
                      isActive ? "bg-surface text-ink-display" : "text-ink-body",
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && <Check className="h-4 w-4 shrink-0 text-accent" />}
                  </button>
                )
              })}
            </div>

            {selected.length > 0 && (
              <div className="flex items-center justify-between gap-2 border-t border-hairline px-3 py-2">
                <span className="text-xs text-ink-muted">
                  {selected.length} of {options.length} selected
                </span>
                <button type="button" className="toggle-button" onClick={() => onChange([])}>
                  Clear all
                </button>
              </div>
            )}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      {selected.length > 0 && (
        <ul aria-label={selectionLabel} className="flex flex-wrap gap-1.5">
          {selected.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => toggle(option)}
                aria-label={`Remove ${option.label}`}
                className="badge badge-neutral inline-flex cursor-pointer items-center gap-1 hover:text-ink-display disabled:cursor-not-allowed disabled:opacity-60"
              >
                {option.label}
                <X aria-hidden="true" className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
